import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn.js';
import { Skeleton } from '../ui/misc.js';

export interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: string;
  /** Fills the tile with brand yellow — for the single headline number. */
  accent?: boolean;
  icon?: ReactNode;
  isLoading?: boolean;
  /**
   * A delta, set on the value's baseline. A `TrendPill`, usually.
   *
   * Beside the number rather than under it: the change and the figure it
   * changed from are one sentence, and a line break between them makes the
   * eye read two.
   */
  trend?: ReactNode;
  /**
   * The foot of the tile — a sparkline, a meter. Given the full width, so
   * a chart here is not competing with the label rail.
   */
  footer?: ReactNode;
  /**
   * An in-app route. Almost every number on a console is a count of rows
   * that live on another page, so almost every number is a way in: given a
   * route, the tile becomes the target and grows the affordances a link
   * owes you — a cursor, a hover, a focus ring.
   */
  to?: string;
  className?: string;
}

export function StatTile({
  label,
  value,
  hint,
  accent = false,
  icon,
  isLoading = false,
  trend,
  footer,
  to,
  className,
}: StatTileProps) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p
          className={cn(
            'text-[11px] font-bold uppercase tracking-widest',
            accent ? 'text-accent-text/60' : 'text-text-muted',
          )}
        >
          {label}
        </p>
        {icon ? (
          <span className={accent ? 'text-accent-text/50' : 'text-text-muted'}>{icon}</span>
        ) : null}
      </div>

      {isLoading ? (
        <Skeleton className="mt-2 h-9 w-24 rounded-lg" />
      ) : (
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <p className="font-display text-3xl font-bold tabular-nums">{value}</p>
          {trend}
        </div>
      )}

      {hint ? (
        <p className={cn('mt-1 text-xs', accent ? 'text-accent-text/60' : 'text-text-muted')}>
          {hint}
        </p>
      ) : null}

      {footer && !isLoading ? <div className="mt-3">{footer}</div> : null}
    </>
  );

  const surface = cn(
    'block rounded-card p-5',
    accent ? 'bg-accent text-accent-text' : 'border border-border-default bg-surface-raised',
    className,
  );

  if (!to) return <div className={surface}>{body}</div>;

  return (
    <Link
      to={to}
      className={cn(
        surface,
        'transition-shadow hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
      )}
    >
      {body}
    </Link>
  );
}
